/**
 * Auto-reload policy — determines if it's safe to auto-apply a pending
 * update without user interaction. The UpdateBanner is always mandatory
 * and visible regardless; this policy only decides whether the watcher
 * may *also* trigger the reload automatically after a short grace period.
 *
 * Two classes of blocks:
 *   - HARD blocks (exam/practice, critical route, stripe return params):
 *     prevent both scheduling and firing.
 *   - SOFT blocks (page hidden, real text-input focused): only enforced
 *     at fire-time. The watcher still schedules the timer so the auto
 *     reload happens once the soft condition clears.
 */

import { isExamOrPracticeActive } from "@/lib/safe-storage";

// Routes where auto-reload is NEVER allowed (banner only, user must tap).
const CRITICAL_ROUTE_PATTERNS: RegExp[] = [
  /^\/$/,
  /^\/auth(\/|$)/,
  /^\/quiz(\/|$)/,
  /^\/exam(\/|$)/,
  /^\/exam-simulation(\/|$)/,
  /^\/real-exam(\/|$)/,
  /^\/practice(\/|$)/,
  /^\/study(\/|$)/,
  /^\/review-bank(\/|$)/,
  /^\/onboarding-quiz(\/|$)/,
  /^\/checkout(\/|$)/,
  /^\/unsubscribe(\/|$)/,
];

// Input types that DO count as text entry. Anything else (button, submit,
// checkbox, radio, hidden, file, range, color, date, time, month, week)
// must NOT block — those are common in dropdowns / toggles / pickers and
// would otherwise spuriously block a safe Dashboard.
const TEXT_INPUT_TYPES = new Set([
  "text",
  "email",
  "password",
  "search",
  "url",
  "tel",
  "number",
  "", // <input> with no type attribute defaults to text
]);

function hasStripeReturnParams(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.has("session_id") || p.has("payment_intent") || p.has("checkout_session_id");
  } catch {
    return false;
  }
}

function isRealTextInputFocused(): boolean {
  try {
    const el = document.activeElement as HTMLElement | null;
    if (!el || el === document.body) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName?.toLowerCase();
    if (tag === "textarea") return true;
    if (tag === "input") {
      const type = ((el as HTMLInputElement).type || "").toLowerCase();
      return TEXT_INPUT_TYPES.has(type);
    }
    return false;
  } catch {
    return false;
  }
}

function matchCriticalRoute(): string | null {
  try {
    const path = window.location?.pathname ?? "";
    const matched = CRITICAL_ROUTE_PATTERNS.find((re) => re.test(path));
    return matched ? `critical_route:${path}` : null;
  } catch {
    return null;
  }
}

export interface AutoReloadDecision {
  safe: boolean;
  /** When unsafe, a short reason tag for logging/debug. */
  reason: string | null;
  /**
   * "hard" = do not schedule (exam/critical-route/stripe).
   * "soft" = caller may schedule and re-check at fire time
   *          (page_hidden, active_form_focus).
   * null when safe.
   */
  severity: "hard" | "soft" | null;
}

export function evaluateAutoReload(): AutoReloadDecision {
  try {
    if (isExamOrPracticeActive()) {
      return { safe: false, reason: "exam_or_practice_active", severity: "hard" };
    }
    const route = matchCriticalRoute();
    if (route) {
      return { safe: false, reason: route, severity: "hard" };
    }
    if (hasStripeReturnParams()) {
      return { safe: false, reason: "stripe_return_params", severity: "hard" };
    }
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return { safe: false, reason: "page_hidden", severity: "soft" };
    }
    if (isRealTextInputFocused()) {
      return { safe: false, reason: "active_form_focus", severity: "soft" };
    }
    return { safe: true, reason: null, severity: null };
  } catch (e) {
    return {
      safe: false,
      reason: `eval_error:${e instanceof Error ? e.message : "unknown"}`,
      severity: "hard",
    };
  }
}
